import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ConversionExplanationDecimalToAll from './ConversionExplanationDecimalToAll';
import ConversionFlow from '../../ConversionFlow';

const ConversionExplanationHexadecimalToAll = ({ conversionType, fromUnit, toUnit, inputValue, result, darkMode }) => {
  const [showIntermediateConversion, setShowIntermediateConversion] = useState(false);
  const [showDetailedStep2, setShowDetailedStep2] = useState(true);
  const [showDetailedConversion, setShowDetailedConversion] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState('');
  const [quizResult, setQuizResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConversionRendered, setIsConversionRendered] = useState(false);

  const handleQuizSubmit = useCallback(() => {
    const isCorrect = quizAnswer.toLowerCase() === result.toLowerCase();
    setQuizResult(isCorrect);
  }, [quizAnswer, result]);

  const renderExampleOrConversionText = useCallback((hexValue, isExample) => (
    isExample ? (
      <p className="mb-4 text-yellow-600 italic">
        Example: Let's convert the hexadecimal number {hexValue} to {toUnit}.
      </p>
    ) : (
      <p className="mb-4 text-green-600">
        Your conversion: {hexValue} (Hexadecimal) to {toUnit}
      </p>
    )
  ), [toUnit]);

  const toggleIntermediateConversion = () => setShowIntermediateConversion(!showIntermediateConversion);
  const toggleDetailedConversion = () => setShowDetailedConversion(!showDetailedConversion);
  const toggleDetailedStep2 = () => setShowDetailedStep2(!showDetailedStep2);

  const getIntermediateConversionExplanation = useCallback((hexValue) => {
    const decimalValue = parseInt(hexValue, 16);
    const steps = hexValue.split('').reverse();

    const renderHexToDecimalConversion = () => (
      <div className="relative mt-12 flex flex-col items-center">
        <div className="absolute top-0 left-0 text-2xl font-bold main-number bg-gray-100 dark:bg-gray-700 p-2 rounded-md shadow-md">
          {hexValue.split('').map((digit, index) => (
            <span key={index} className={`transition-colors duration-300 ${steps.length - 1 - index === currentStep ? 'text-blue-500 dark:text-blue-400' : 'dark:text-white'}`}>
              {digit}
            </span>
          ))}
          <sub className="dark:text-white">16</sub>
        </div>
        <div className="flex flex-col items-center space-y-4 mt-16">
          {steps.map((digit, index) => {
            const value = parseInt(digit, 16) * Math.pow(16, index);
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center space-x-4 p-4 rounded-lg shadow-md ${isActive ? 'bg-blue-50 dark:bg-blue-900' :
                    isCompleted ? 'text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'
                  } transition-all duration-300`}
              >
                <div className={`w-12 text-right digit ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{digit}</div>
                <div className="w-8 text-center dark:text-white">×</div>
                <div className="w-16 text-center group relative dark:text-white">
                  16<sup>{index}</sup>
                  <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800 text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    16{index > 0 ? <sup>{index}</sup> : ''} = {Math.pow(16, index)}
                  </span>
                </div>
                <div className="w-8 text-center dark:text-white">=</div>
                <div className={`w-20 text-left calculation ${isActive ? 'text-blue-500 dark:text-blue-400 font-bold' : 'dark:text-white'} transition-colors duration-300`}>{value}</div>
              </motion.div>
            );
          })}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: currentStep === steps.length ? 1 : 0, y: currentStep === steps.length ? 0 : 10 }}
            className="font-bold text-xl mt-6 pt-4 border-t border-gray-300 dark:border-gray-600 dark:text-white"
          >
            Sum: {decimalValue} (Decimal)
          </motion.div>
        </div>
      </div>
    );

    return (
      <AnimatePresence>
        {showIntermediateConversion && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`mt-6 p-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg shadow-lg relative overflow-hidden`}
            style={{
              background: darkMode
                ? 'linear-gradient(145deg, #2d3748, #1a202c)'
                : 'linear-gradient(145deg, #f7fafc, #edf2f7)'
            }}
          >
            <h4 className="text-2xl font-semibold mb-4 flex items-center">
              <FaCalculator className="mr-2" />
              Hexadecimal to Decimal Conversion
            </h4>
            <p className="mb-6 leading-relaxed">
              To convert hexadecimal to decimal, we multiply each digit by 16 raised to the power of its position (starting from 0) and sum the results.
            </p>

            {renderHexToDecimalConversion()}

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === 0}
              >
                <FaChevronLeft className="mr-2" /> Previous
              </button>
              <button
                onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                className={`px-4 py-2 flex items-center ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white rounded-md transition-all duration-300 transform hover:scale-105`}
                disabled={currentStep === steps.length}
              >
                Next <FaChevronRight className="ml-2" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }, [showIntermediateConversion, darkMode, currentStep]);

  const getHexToDecimalExplanation = useCallback((hexValue, isExample, exampleResult) => {
    const decimalValue = parseInt(hexValue, 16);

    const steps = hexValue.split('').reverse().map((digit, index) => ({
      digit: parseInt(digit, 16),
      power: index,
      value: parseInt(digit, 16) * Math.pow(16, index)
    }));

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Hexadecimal to Decimal Conversion Works</h3>
        {renderExampleOrConversionText(hexValue, isExample)}
        <p className="mb-4">
          To convert hexadecimal to decimal, we multiply each digit by 16 raised to the power of its position (starting from 0) and sum the results.
        </p>
        <div className="overflow-x-auto">
          <table className={`w-full text-left ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <thead>
              <tr>
                <th className="px-4 py-2">Digit</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Calculation</th>
                <th className="px-4 py-2">Value</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                >
                  <td className="border px-4 py-2">{step.digit}</td>
                  <td className="border px-4 py-2">{step.power}</td>
                  <td className="border px-4 py-2">{step.digit} × 16<sup>{step.power}</sup></td>
                  <td className="border px-4 py-2">{step.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4">
          Sum of all values: <span className="font-bold text-purple-500">{decimalValue}</span> (Decimal)
        </p>
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{hexValue}</span> (Hexadecimal) = <span className="text-purple-500">{isExample ? exampleResult : decimalValue}</span> (Decimal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText]);

  const getHexToOtherBaseExplanation = useCallback((hexValue, isExample, base, baseLabel, exampleResult) => {
    const decimalValue = parseInt(hexValue, 16);
    const convertedValue = isExample ? exampleResult : result;

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{hexValue}</span><sub className="text-black dark:text-white">16</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{convertedValue}</span><sub className="text-black dark:text-white">{base}</sub>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Hexadecimal</p>
            <p className="text-lg">{hexValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">{baseLabel}</p>
            <p className="text-lg">{convertedValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Hexadecimal to {baseLabel} Conversion Works</h3>
        {renderExampleOrConversionText(hexValue, isExample)}
        <p className="mb-4">
          To convert a hexadecimal number to {baseLabel}, we first convert the hexadecimal number to decimal, and then we convert the decimal to {baseLabel}.
        </p>
        <ConversionFlow
          steps={['Hexadecimal', 'Decimal', baseLabel]}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert hexadecimal to decimal: {hexValue}<sub>16</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(hexValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to {baseLabel}:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{hexValue}</span><sub className="text-black">16</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                  <sub className="text-black">{base}</sub>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden"
                  >
                    <ConversionExplanationDecimalToAll
                      conversionType="number"
                      fromUnit="Decimal"
                      toUnit={baseLabel}
                      inputValue={decimalValue.toString()}
                      result={convertedValue}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{hexValue}<sub className="text-black">16</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{convertedValue}<sub className="text-black">{base}</sub></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{hexValue}</span> (Hexadecimal) = <span className="text-green-500">{convertedValue}</span> ({baseLabel})
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getHexToOctalExplanation = useCallback((hexValue, isExample, exampleResult) => {
    const decimalValue = parseInt(hexValue, 16);
    const octalValue = isExample ? exampleResult : result;

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{hexValue}</span><sub className="text-black dark:text-white">16</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{octalValue}</span><sub className="text-black dark:text-white">8</sub>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Hexadecimal</p>
            <p className="text-lg">{hexValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Octal</p>
            <p className="text-lg">{octalValue}</p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Hexadecimal to Octal Conversion Works</h3>
        {renderExampleOrConversionText(hexValue, isExample)}
        <p className="mb-4">
          To convert a hexadecimal number to octal, we first convert the hexadecimal number to decimal, and then we convert the decimal to octal.
        </p>
        <ConversionFlow
          steps={['Hexadecimal', 'Decimal', 'Octal']}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert hexadecimal to decimal: {hexValue}<sub>16</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(hexValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to octal:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{hexValue}</span><sub className="text-black">16</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                  <sub className="text-black">8</sub>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden"
                  >
                    <ConversionExplanationDecimalToAll
                      conversionType="number"
                      fromUnit="Decimal"
                      toUnit="Octal"
                      inputValue={decimalValue.toString()}
                      result={octalValue}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{hexValue}<sub className="text-black">16</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{octalValue}<sub className="text-black">8</sub></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{hexValue}</span> (Hexadecimal) = <span className="text-green-500">{octalValue}</span> (Octal)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getHexToScientificNotationExplanation = useCallback((hexValue, isExample, exampleResult) => {
    const decimalValue = parseInt(hexValue, 16);
    const scientificNotation = isExample ? exampleResult : result;

    const [coefficient, exponent] = scientificNotation.split('e').map(part => parseFloat(part));

    const CompactExplanation = () => (
      <div className="mt-4 p-6 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900 dark:to-green-900 rounded-lg shadow-md">
        <p className="text-center font-bold text-2xl mb-4">
          <span className="text-blue-600 dark:text-blue-400">{hexValue}</span><sub className="text-black dark:text-white">16</sub> →
          <span className="text-purple-600 dark:text-purple-400">{decimalValue}</span><sub className="text-black dark:text-white">10</sub> →
          <span className="text-green-600 dark:text-green-400">{coefficient} × 10<sup>{exponent}</sup></span>
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="font-semibold text-blue-600 dark:text-blue-400">Hexadecimal</p>
            <p className="text-lg">{hexValue}</p>
          </div>
          <div>
            <p className="font-semibold text-purple-600 dark:text-purple-400">Decimal</p>
            <p className="text-lg">{decimalValue}</p>
          </div>
          <div>
            <p className="font-semibold text-green-600 dark:text-green-400">Scientific Notation</p>
            <p className="text-lg">{coefficient} × 10<sup>{exponent}</sup></p>
          </div>
        </div>
        <p className="mt-4 text-center text-gray-600 dark:text-gray-300 italic">
          Click "Show Details" for step-by-step explanation
        </p>
      </div>
    );

    return (
      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-4">How Hexadecimal to Scientific Notation Conversion Works</h3>
        {renderExampleOrConversionText(hexValue, isExample)}
        <p className="mb-4">
          To convert a hexadecimal number to scientific notation, we first convert the hexadecimal number to decimal, and then we convert the decimal to scientific notation.
        </p>
        <ConversionFlow
          steps={['Hexadecimal', 'Decimal', 'Scientific Notation']}
          darkMode={darkMode}
        />
        <ol className="list-decimal list-inside mb-4">
          <li className="mb-2">
            Convert hexadecimal to decimal: {hexValue}<sub>16</sub> = {decimalValue}<sub>10</sub>
            <button
              onClick={toggleIntermediateConversion}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showIntermediateConversion ? 'Hide Details' : 'Show Details'}
            </button>
          </li>
          {getIntermediateConversionExplanation(hexValue)}
          <li className="mb-2">
            Now, we convert the decimal number <span className="font-bold text-blue-500">{decimalValue}</span> to scientific notation:
            <button
              onClick={toggleDetailedStep2}
              className={`ml-2 px-2 py-1 text-sm ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
            >
              {showDetailedStep2 ? 'Hide Details' : 'Show Details'}
            </button>
            {showDetailedStep2 ? (
              <>
                <p className="mt-2 text-center font-semibold">
                  <span className="text-blue-500">{hexValue}</span><sub className="text-black">16</sub> →
                  <span className="text-blue-500">{decimalValue}</span><sub className="text-black">10</sub> →
                  <span className="text-blue-500">...</span>
                </p>
                <AnimatePresence>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 p-4 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden"
                  >
                    <ConversionExplanationDecimalToAll
                      conversionType="number"
                      fromUnit="Decimal"
                      toUnit="Scientific Notation"
                      inputValue={decimalValue.toString()}
                      result={scientificNotation}
                      darkMode={darkMode}
                    />
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <CompactExplanation />
            )}
          </li>
        </ol>
        {showDetailedStep2 && (
          <>
            <p className="mt-4 text-center font-semibold text-lg">
              Complete conversion flow:
            </p>
            <p className="mt-2 text-center">
              <span className="font-bold text-blue-500">{hexValue}<sub className="text-black">16</sub></span> →
              <span className="font-bold text-blue-500">{decimalValue}<sub className="text-black">10</sub></span> →
              <span className="font-bold text-green-500">{coefficient} × 10<sup>{exponent}</sup></span>
            </p>
          </>
        )}
        <p className="mt-4 font-bold">
          Therefore, <span className="text-blue-500">{hexValue}</span> (Hexadecimal) = <span className="text-green-500">{coefficient} × 10<sup>{exponent}</sup></span> (Scientific Notation)
        </p>
      </div>
    );
  }, [darkMode, renderExampleOrConversionText, getIntermediateConversionExplanation, showIntermediateConversion, showDetailedStep2, result, toggleIntermediateConversion, toggleDetailedStep2]);

  const getExplanation = useMemo(() => {
    const hexValue = inputValue || '2A';
    const isExample = !result;
    const exampleResult = isExample ? 
      (toUnit === 'Scientific Notation' ? 
        parseFloat(parseInt(hexValue, 16)).toExponential() : 
        parseInt(hexValue, 16).toString(toUnit === 'Binary' ? 2 : toUnit === 'Decimal' ? 10 : toUnit === 'Octal' ? 8 : toUnit === 'Base-36' ? 36 : 16).toUpperCase()
      ) : result;

    if (toUnit === 'Decimal') {
      return getHexToDecimalExplanation(hexValue, isExample, exampleResult);
    } else if (toUnit === 'Binary') {
      return getHexToOtherBaseExplanation(hexValue, isExample, 2, 'Binary', exampleResult);
    } else if (toUnit === 'Octal') {
      return getHexToOctalExplanation(hexValue, isExample, exampleResult);
    } else if (toUnit === 'Hexadecimal') {
      return getHexToOtherBaseExplanation(hexValue, isExample, 16, 'Hexadecimal', exampleResult);
    } else if (toUnit === 'Base-36') {
      return getHexToOtherBaseExplanation(hexValue, isExample, 36, 'Base-36', exampleResult);
    } else if (toUnit === 'Base-64') {
      return getHexToOtherBaseExplanation(hexValue, isExample, 64, 'Base-64', exampleResult);
    } else if (toUnit === 'Roman Numeral') {
      return getHexToOtherBaseExplanation(hexValue, isExample, 'Roman Numeral', 'Roman Numeral', exampleResult);
    } else if (toUnit === 'Scientific Notation') {
      return getHexToScientificNotationExplanation(hexValue, isExample, exampleResult);
    }
  }, [inputValue, toUnit, result, getHexToDecimalExplanation, getHexToOtherBaseExplanation, getHexToOctalExplanation, getHexToScientificNotationExplanation]);

  useEffect(() => {
    if (showIntermediateConversion) {
      const timer = setTimeout(() => setIsConversionRendered(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsConversionRendered(false);
    }
  }, [showIntermediateConversion]);

  return (
    <div className={`p-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'} font-sans leading-relaxed`}>
      {getExplanation}
      <div className="mt-6">
        <button
          onClick={() => setShowQuiz(!showQuiz)}
          className={`px-4 py-2 ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white rounded-md transition-colors`}
        >
          {showQuiz ? 'Hide Quiz' : 'Test Your Knowledge'}
        </button>
      </div>

      {showQuiz && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-4 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-md`}
        >
          <h4 className="font-semibold">Quick Quiz:</h4>
          <p>Convert the hexadecimal number {inputValue || '2A'} to {toUnit.toLowerCase()}:</p>
          <input
            type="text"
            value={quizAnswer}
            onChange={(e) => setQuizAnswer(e.target.value)}
            className={`mt-2 px-2 py-1 border ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'} rounded-md`}
          />
          <button
            onClick={handleQuizSubmit}
            className={`ml-2 px-4 py-1 ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white rounded-md transition-colors`}
          >
            Submit
          </button>
          {quizResult !== null && (
            <p className={`mt-2 text-lg font-bold ${quizResult ? 'text-green-500' : 'text-red-500'}`}>
              {quizResult ? 'Correct!' : `Try again! The correct answer is ${result}.`}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default ConversionExplanationHexadecimalToAll;